import React, { Dispatch, SetStateAction, useRef } from 'react';

import {
  Drawer,
  Typography,
  IconButton,
  MuiThemeProvider,
  makeStyles
} from '@material-ui/core';
import { Form, yupSchemaToValidationFunc, FormValues } from 'components/Forms';
import { SecondaryButton, SubmitButton } from 'components/Forms/Buttons';
import { feedbackButtonZIndex } from 'styles/defaultTheme';
import { FormApi } from 'final-form';
import {
  useCapacityTypesNotDeletedData,
  useGeographicAreasData,
  useReservedCapacityItem
} from 'store/selectors';
import CloseIcon from '@material-ui/icons/Close';
import {
  editReserveCapacity,
  fetchReserveCapacities
} from 'store/slices/reserveCapacity';
import { useDispatch } from 'react-redux';
import { DayOfWeek } from 'models/Adjustment';
import { setSnackbar, SnackbarStatus } from 'store/slices/snackbar';
import { DATE_ISO_SHORT } from 'constants/dates';
import { DateTime } from 'luxon';
import {
  EditReserveCapacityFormFields,
  getEditReserveCapacityFields,
  validationSchema
} from './editReserveCapacityFormConfig';
import reserveCapacityTheme from './reserveCapacityTheme';

const useDrawerStyles = makeStyles(
  theme => ({
    drawer: {
      width: theme.containers.small,
      padding: theme.spacing(4, 3, 2, 3)
    },
    header: {
      display: 'flex',
      paddingBottom: theme.spacing(4),
      alignItems: 'flex-start',
      justifyContent: 'space-between'
    },
    closeButton: {
      '& .MuiSvgIcon-root': {
        fontSize: theme.spacing(3),
        cursor: 'pointer'
      },
      padding: 0
    },
    submitButton: {
      marginLeft: theme.spacing(2)
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingTop: theme.spacing(3)
    }
  }),
  { name: 'EditCapacityTypeDrawer' }
);

interface EditReserveCapacityDrawerProps {
  isOpen: boolean;
  toggleDrawer: () => void;
  reserveCapacityId: string;
  setEditedId: Dispatch<SetStateAction<string | undefined>>;
}

export default function EditReserveCapacityDrawer({
  isOpen,
  toggleDrawer,
  reserveCapacityId,
  setEditedId
}: EditReserveCapacityDrawerProps) {
  const drawerClasses = useDrawerStyles();
  const formRef = useRef<FormApi>();
  const dispatch = useDispatch();

  const submitForm = async (values: FormValues) => {
    if (formRef.current?.getState().dirty) {
      const selectedDayValues = Object.entries(
        values[EditReserveCapacityFormFields.EffectiveDays] as Record<
          keyof typeof DayOfWeek,
          boolean
        >
      ).reduce(
        (acc, [day, isSelected]) => (isSelected ? [...acc, day] : acc),
        [] as string[]
      );
      await dispatch(
        editReserveCapacity({
          id: reserveCapacityId,
          area: values[EditReserveCapacityFormFields.Area] as string,
          capacityType: values[
            EditReserveCapacityFormFields.CapacityType
          ] as string,
          startDate: (values[
            EditReserveCapacityFormFields.ValidFrom
          ] as DateTime).toFormat(DATE_ISO_SHORT),
          endDate: (values[
            EditReserveCapacityFormFields.ValidTo
          ] as DateTime).toFormat(DATE_ISO_SHORT),
          percentage: Number(values[EditReserveCapacityFormFields.Percentage]),
          releaseAt: Number(values[EditReserveCapacityFormFields.ReleaseAt]),
          days: selectedDayValues as DayOfWeek[]
        })
      );
      dispatch(fetchReserveCapacities());
      setEditedId(reserveCapacityId);
    }
    toggleDrawer();
  };

  const handleDrawerClose = () => {
    const formState = formRef.current?.getState();

    if (formState?.dirty) {
      dispatch(
        setSnackbar({
          message: 'Your changes have been discarded.',
          show: true,
          status: SnackbarStatus.Success,
          useNewSnackbar: true
        })
      );
    }
    toggleDrawer();
  };

  const handleDrawerSubmit = () => formRef.current?.submit();

  const editReserveCapacityFields = getEditReserveCapacityFields(
    useGeographicAreasData(),
    useCapacityTypesNotDeletedData()
  );

  const reserveCapacityToEdit = useReservedCapacityItem(reserveCapacityId);

  const initialFormValues: FormValues = reserveCapacityToEdit
    ? {
        [EditReserveCapacityFormFields.Area]: reserveCapacityToEdit.area,
        [EditReserveCapacityFormFields.CapacityType]:
          reserveCapacityToEdit.capacityType,
        [EditReserveCapacityFormFields.ValidFrom]: DateTime.fromISO(
          reserveCapacityToEdit.startDate
        ),
        [EditReserveCapacityFormFields.ValidTo]: DateTime.fromISO(
          reserveCapacityToEdit.endDate
        ),
        [EditReserveCapacityFormFields.Percentage]: String(
          reserveCapacityToEdit.percentage
        ),
        [EditReserveCapacityFormFields.ReleaseAt]: String(
          reserveCapacityToEdit.releaseAt
        ),
        [EditReserveCapacityFormFields.EffectiveDays]: reserveCapacityToEdit.days.reduce(
          (prev, day) => ({
            ...prev,
            [day]: true
          }),
          {}
        )
      }
    : {};

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={isOpen}
      onClose={handleDrawerClose}
      classes={{
        paper: drawerClasses.drawer
      }}
      ModalProps={{
        style: {
          // This makes sure that the drawer is on top of the "Feedback" button.
          zIndex: feedbackButtonZIndex
        }
      }}
      data-testid="editReserveCapacityDrawer"
    >
      <header className={drawerClasses.header}>
        <Typography variant="h1">Edit capacity</Typography>
        <IconButton
          aria-label="close"
          onClick={handleDrawerClose}
          className={drawerClasses.closeButton}
          size="medium"
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </header>
      <MuiThemeProvider theme={reserveCapacityTheme}>
        <Form
          validationFunc={yupSchemaToValidationFunc(validationSchema)}
          fields={editReserveCapacityFields}
          onSubmit={submitForm}
          goBackToPreviousPage={false}
          initialValues={initialFormValues}
        >
          {({ form }) => {
            formRef.current = form;
          }}
        </Form>
      </MuiThemeProvider>
      <footer className={drawerClasses.footer}>
        <SecondaryButton
          size="medium"
          onClick={handleDrawerClose}
          testId="editReserveCapacityCancel"
        >
          Cancel
        </SecondaryButton>
        <SubmitButton
          size="medium"
          onClick={handleDrawerSubmit}
          testId="editReserveCapacitySubmit"
          className={drawerClasses.submitButton}
        >
          Save
        </SubmitButton>
      </footer>
    </Drawer>
  );
}
